/**
 * BTS — LOVE YOURSELF 轉 ‘Tear’ (2018)
 *
 * Source of truth: `/Users/ivanmunguia/projects/nyx/tmp/bts_love_yourself.json`.
 * Analytical prose is carried over verbatim wherever the local schema allows.
 * Schema-fit transformations:
 *
 *   - hero / deepDiveLine / sectionTitle: decomposed from
 *     `album_form_and_behavior.form_behavior_gap`.
 *   - tensions[].anchors: parsed from `"<Track>: <note>"` strings into the
 *     existing `{ track, note }` shape.
 *   - signature_moments[].pull / .why: split from the single
 *     `why_it_matters` paragraph at the first sentence boundary.
 *   - `data_integrity_notes` is intentionally not shipped because the landing
 *     schema has no rendered slot for generator audit notes.
 */

import type { AlbumLibraryEntry, DeepDive } from '@/types/album';

export const library = {
  id: 'love-yourself-tear',
  artist: 'BTS',
  album: 'LOVE YOURSELF 轉 ‘Tear’',
  year: 2018,
  genre: 'K-pop',
  hook:
    'Es el disco que oscurece el proyecto sin perder ambición pop, y por eso se siente menos como una transición suave que como el momento en que el grupo decide mirar el daño de frente.',
  palette: { bg: '#0D1020', fg: '#F1E7F2', accent: '#C79BE8' },
  typeStyle: 'wide-display',
  available: true,
  hero: {
    apparent: 'un gran paquete pop sobre el desamor',
    key: 'un ciclo de negación, alivio temporal y confesión final',
    coda: 'que cada vez estrecha más el cerco.',
  },
  deepDiveLine: {
    apparent: 'un gran paquete pop sobre el desamor',
    key: 'un ciclo de negación, alivio temporal y confesión final que cada vez estrecha más el cerco',
  },
  sectionTitle: {
    pre: 'LOVE YOURSELF 轉 ‘Tear’, leído como un',
    em: 'ciclo de negación, alivio temporal y confesión final',
    post: 'que cada vez estrecha más el cerco.',
  },
  songsTitle: 'Tres canciones, leídas con cuidado.',
  footerCredit: 'Análisis original · LOVE YOURSELF 轉 ‘Tear’ · BTS · 2018',
  coverImage: '/images/love_yourself.jpg',
} as const satisfies AlbumLibraryEntry;

export const deepDive: DeepDive = {
  artist: 'BTS',
  title: 'LOVE YOURSELF 轉 ‘Tear’',
  year: '2018',
  position_label: 'punto de quiebre y profundización',
  apparent_form:
    'Parece un álbum pop de gran escala, muy secuenciado, dentro del proyecto LOVE YOURSELF, con sencillos grandes, baladas de vocal line y piezas de rap pensadas para cubrir todo el rango emocional del grupo.',
  actual_behavior:
    'En secuencia, el álbum funciona como una caída controlada que aprende a respirar a mitad del trayecto y luego remata con una herida abierta. Los primeros temas fijan una atmósfera de seducción rota y autoengaño; el bloque central afloja el pecho con groove, consuelo y deseo de escapatoria; el cierre devuelve todo al punto más crudo y deja claro que la ruptura nunca estuvo resuelta.',
  form_behavior_gap:
    'Parece un gran paquete pop sobre el desamor, pero se mueve como un ciclo de negación, alivio temporal y confesión final que cada vez estrecha más el cerco.',
  previous_context:
    'El periodo inmediatamente anterior en coreano, LOVE YOURSELF 承 \'Her\', vendía brillo, deseo y color, con "DNA" como cara luminosa; aquí ese impulso se agrieta y el proyecto muestra el costo emocional de sostener una imagen amable.',
  next_context:
    'Después llegó LOVE YOURSELF 結 \'Answer\', que recompuso esta oscuridad dentro de un cierre más amplio y conciliador; más tarde, la era MAP OF THE SOUL expandió la introspección hacia una escala conceptual todavía mayor.',
  position_summary:
    'Dentro de la discografía coreana de BTS, LOVE YOURSELF 轉 ‘Tear’ ocupa el lugar donde la narrativa del amor propio deja de sonar aspiracional y se vuelve una crisis. Es el disco que oscurece el proyecto sin perder ambición pop, y por eso se siente menos como una transición suave que como el momento en que el grupo decide mirar el daño de frente.',

  arch_type: 'hinge-driven',
  arch_summary:
    'El álbum está construido alrededor de un centro que afloja la presión antes de endurecerla otra vez. Arranca en la fractura, encuentra una zona media de deriva y consuelo, y usa el final para demostrar que ese alivio nunca cerró la herida.',
  arch_opening:
    'Los tres primeros cortes dicen la verdad desde el principio, aunque lo hacen con formas distintas. "Intro: Singularity" abre con un R&B elegante y torcido, "FAKE LOVE" convierte esa torsión en himno, y "The Truth Untold" reduce la escala para dejar la vulnerabilidad casi desnuda.',
  arch_middle:
    'Del 4 al 9 está el verdadero centro de gravedad. "134340", "Paradise", "Love Maze" y "Magic Shop" cambian la textura del disco con ritmos más elásticos, bajos suaves y un tono de cercanía que parece ofrecer salida; luego "Airplane, Pt. 2" y "Anpanman" empujan hacia afuera con color y movimiento. Ese bloque medio no niega la herida del inicio, pero sí enseña cómo el grupo la administra: con groove, camaradería y pequeños refugios.',
  arch_closing:
    'El cierre rechaza una salida amable. "So What" parece ofrecer descarga colectiva, casi una euforia de festival, y "Outro: Tear" usa ese impulso como trampolín para un final áspero, rápido y rabioso que deja el cuerpo tenso en lugar de liberado.',
  arch_sequencing_note:
    'La secuencia coloca dos canciones de alivio comunitario muy tarde, "Anpanman" y "So What", para que el golpe de "Outro: Tear" caiga con más fuerza.',

  tensions: [
    {
      label: 'Querer ser amado vs sentir que mostrarse destruye el amor',
      description:
        'El disco quiere intimidad, pero la vive como peligro. Desde "Intro: Singularity" ya aparece una identidad partida, sensual por fuera y vacía por dentro. "FAKE LOVE" lleva eso al centro del álbum con una interpretación gigantesca sobre un vínculo sostenido por máscara, y "The Truth Untold" lo vuelve casi insoportable al convertir la confesión en algo que nunca termina de cruzar la puerta.',
      anchors: [
        { track: 'FAKE LOVE', note: 'el estribillo convierte el amor en una actuación agotadora' },
        { track: 'The Truth Untold', note: 'la voz se acerca mientras la letra insiste en ocultarse' },
        { track: 'Intro: Singularity', note: 'apertura seductora con sensación de desdoblamiento' },
      ],
    },
    {
      label: 'Moverse sin parar vs seguir emocionalmente estancado',
      description:
        'Muchos cortes hablan de moverse, viajar, seguir, girar, pero el movimiento rara vez trae resolución. "134340" imagina una existencia relegada a dar vueltas lejos del centro; "Paradise" ofrece descanso frente a la presión de tener destino; "Airplane, Pt. 2" suena libre y cosmopolita, aunque ese vuelo también sirve para no quedarse quieto el tiempo suficiente como para sentir la pérdida. El álbum avanza mucho y aun así se siente atrapado.',
      anchors: [
        { track: '134340', note: 'la metáfora del planeta expulsado convierte el abandono en órbita' },
        { track: 'Paradise', note: 'rechazo del mandato de correr sin descanso' },
        { track: 'Airplane, Pt. 2', note: 'fantasía de movimiento global con cansancio debajo' },
      ],
    },
    {
      label: 'Buscar consuelo compartido vs terminar hablando desde una herida irreparable',
      description:
        'La segunda mitad ofrece canciones que abrazan, levantan y convierten la fragilidad en vínculo colectivo. "Magic Shop" y "Anpanman" proponen una comunidad donde la imperfección sirve, ayuda y acompaña. Pero el álbum no deja que ese calor cierre la historia: "Outro: Tear" irrumpe con rabia, duelo y resentimiento, como si el propio acto de consolar hubiese sido una pausa necesaria antes de decir la verdad completa.',
      anchors: [
        { track: 'Magic Shop', note: 'promesa explícita de refugio mutuo entre artista y oyente' },
        { track: 'Anpanman', note: 'heroísmo torpe, sin épica limpia' },
        { track: 'Outro: Tear', note: 'cierre de rap line que rompe el tono de consuelo' },
      ],
    },
  ],

  signature_moments: [
    {
      track: 'FAKE LOVE',
      anchor: 'el cambio de clima entre la amenaza elegante del verso y el estribillo que se abre como confesión rota',
      pull: 'Ese salto explica casi todo el álbum.',
      why: 'BTS presenta una superficie de control, estilo y precisión, y en el momento del estribillo la canción deja entrar el derrumbe emocional que el resto del disco va administrando por partes. Si escuchas esa grieta con atención, la mezcla de glamour y dolor del álbum entero se vuelve mucho más clara.',
    },
    {
      track: 'Outro: Tear',
      anchor: 'la entrada feroz de la rap line y el cierre sin catarsis limpia',
      pull: 'Este final cambia la lectura de toda la segunda mitad.',
      why: 'Las canciones de consuelo anteriores dejan de sonar como resolución y pasan a sentirse como respiraciones prestadas antes del verdadero estallido. Cuando llega "Outro: Tear", el disco revela que su centro no era sanar rápido, sino soportar el momento exacto en que el vínculo se rompe y todavía sigue doliendo.',
    },
  ],

  motifs: [
    'La máscara o la versión falsa de uno mismo reaparece en Intro: Singularity, FAKE LOVE, The Truth Untold y Outro: Tear, siempre uniendo deseo con ocultamiento.',
    'El movimiento como fuga recorre 134340, Paradise, Love Maze, Airplane, Pt. 2 y So What, donde avanzar sirve tanto para sobrevivir como para aplazar el golpe emocional.',
    'Las imágenes de refugio y salvación compartida atraviesan Love Maze, Magic Shop, Anpanman y, de forma invertida, The Truth Untold, donde el refugio deseado nunca llega a abrirse.',
  ],

  enduring:
    'Este álbum sigue vivo porque entendió algo muy preciso sobre BTS en su momento de expansión global: podían crecer hacia afuera y al mismo tiempo volverse más incómodos por dentro. Tiene sencillos enormes, producción pulida y canciones pensadas para estadios, pero la emoción que organiza todo es la fractura. Esa fricción le da peso histórico. LOVE YOURSELF 轉 ‘Tear’ no se recuerda sólo por lo bien hecho que está, sino porque mostró que el grupo podía usar el tamaño del pop para decir cosas más feas, más tristes y más honestas sin perder contacto con su público.',

  tracks: [
    { n: 1, title: 'Intro: Singularity', ms: 197000, hasMeaning: true },
    { n: 2, title: 'FAKE LOVE', ms: 242000, hasMeaning: true },
    { n: 3, title: 'The Truth Untold', ms: 242000, hasMeaning: true },
    { n: 4, title: '134340', ms: 230000, hinge: true },
    { n: 5, title: 'Paradise', ms: 211000 },
    { n: 6, title: 'Love Maze', ms: 221213 },
    { n: 7, title: 'Magic Shop', ms: 276000 },
    { n: 8, title: 'Airplane, Pt. 2', ms: 219000 },
    { n: 9, title: 'Anpanman', ms: 234000 },
    { n: 10, title: 'So What', ms: 282186 },
    { n: 11, title: 'Outro: Tear', ms: 285000 },
  ],

  songs_with_meaning: ['Fake Love', 'The Truth Untold', 'Singularity'],
};
